import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ChevronLeft, ChevronRight, ExternalLink, User, Eye } from "lucide-react";
import { getReadingTime } from "@/lib/readingTime";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import ArticleCard from "@/components/ArticleCard";
import ReadingProgress from "@/components/ReadingProgress";
import ReadingToolbar from "@/components/ReadingToolbar";
import ShareButtons from "@/components/ShareButtons";
import { SEO, ArticleSchema, BreadcrumbSchema } from "@/components/SEO";
import { READING_MODES, FONT_FAMILIES, getReadingMode, getFontSize, getFontFamily } from "@/lib/readingMode";
import api from "@/lib/api";

function HeroImage({ src, alt, category = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="w-full aspect-[2/1] bg-[#0f2b4c] bg-[radial-gradient(circle_at_30%_40%,rgba(56,189,248,0.08)_0%,transparent_60%)] flex flex-col items-center justify-center gap-4 rounded-sm">
        <img src="https://customer-assets.emergentagent.com/job_1b33c282-eb60-405a-be45-cbe3617d967c/artifacts/rhqju155_OFAlycee_center_logo_blue.png" alt="OFALycée" className="h-12 opacity-30" />
        {category && <span className="text-sm font-medium tracking-widest uppercase text-sky-400/60">{category}</span>}
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-full rounded-sm" data-testid="article-hero-image" onError={() => setFailed(true)} />;
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingMode, setReadingMode] = useState(getReadingMode);
  const [fontSize, setFontSize] = useState(getFontSize);
  const [fontFamily, setFontFamily] = useState(getFontFamily);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then(res => setArticle(res.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const isNewsletter = article?.is_newsletter;

  const toc = useMemo(() => {
    if (!article?.content || isNewsletter) return [];
    const regex = /<h([23])[^>]*>(.*?)<\/h[23]>/gi;
    const items = [];
    let match;
    while ((match = regex.exec(article.content)) !== null) {
      const raw = match[2].replace(/<[^>]+>/g, '');
      const text = new DOMParser().parseFromString(raw, "text/html").body.textContent.trim();
      if (!text) continue; // Skip empty headings
      const id = text.toLowerCase().replace(/[^a-z0-9\u00C0-\u024F]+/gi, '-').replace(/(^-|-$)/g, '');
      items.push({ level: parseInt(match[1]), text, id });
    }
    return items;
  }, [article, isNewsletter]);

  const processedContent = useMemo(() => {
    if (!article?.content) return "";
    let content = article.content;
    // Add TOC anchors to headings — inject id + scroll offset on each h2/h3
    let tocIndex = 0;
    content = content.replace(/<h([23])([^>]*)>/gi, (match, level, attrs) => {
      if (tocIndex < toc.length) {
        const item = toc[tocIndex];
        tocIndex++;
        return `<h${level}${attrs} id="${item.id}" style="scroll-margin-top:100px">`;
      }
      return match;
    });
    // Fix footnotes: add id anchors + scroll offset to footnote definitions like <p><strong>[1]</strong>
    content = content.replace(/<p><strong>\[(\d+)\]<\/strong>/g, '<p id="fn$1" style="scroll-margin-top:120px"><strong>[$1]</strong>');
    // Fix footnote links: make them superscript and scroll on-page, normalize #fn2b → #fn2
    content = content.replace(/<a\s+[^>]*href="(#fn(\d+)[^"]*)"[^>]*>(\[\d+\])<\/a>/g,
      (_, href, num, label) => `<sup><a class="text-ofa-blue no-underline text-[0.75em] font-semibold" href="#fn${num}">${num}</a></sup>`);
    return content;
  }, [article, toc]);

  const category = article?.category_details?.[0];
  const date = article?.publication_date ? new Date(article.publication_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "";
  const readingTime = getReadingTime(article?.content);

  const author = article?.author;
  const authors = article?.authors || (author ? [author] : []);

  if (loading) return <div className="max-w-4xl mx-auto px-6 md:px-12 py-20"><div className="animate-pulse space-y-8"><div className="h-8 bg-muted w-1/3" /><div className="h-12 bg-muted w-3/4" /><div className="h-96 bg-muted" /></div></div>;
  if (!article) return <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 text-center"><h1 className="font-heading text-4xl mb-4">Article non trouve</h1><Link to="/blog" className="text-primary hover:underline">Retour au blog</Link></div>;

  // Newsletter without full content — redirect to external HubSpot link
  if (article.has_full_content === false && article.newsletter_url) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 text-center" data-testid="newsletter-redirect">
        {article.featured_image && (
          <img src={article.featured_image} alt={article.title} className="w-full max-w-md mx-auto rounded-lg mb-8 shadow-md" />
        )}
        <h1 className="font-heading text-3xl md:text-4xl mb-4">{article.title}</h1>
        <p className="text-muted-foreground text-lg mb-8">
          Ce numéro est hébergé sur notre plateforme HubSpot.
        </p>
        <a href={article.newsletter_url} target="_blank" rel="noopener noreferrer" data-testid="read-on-hubspot-btn">
          <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-8 py-3 text-white text-base">
            Accéder à la Toile <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </a>
        <div className="mt-6">
          <Link to="/la-toile" className="text-sm text-primary hover:underline" data-testid="back-to-toile-link">
            ← Retour à La Toile d'OFALycée
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={article.seo_title || article.title}
        description={article.meta_description || article.excerpt || article.title}
        image={article.featured_image || undefined}
        url={`/article/${slug}`}
        type="article"
        article={{ published_at: article.published_at, categories: article.category_details?.map(c => c.name) || [] }}
      />
      <ArticleSchema article={article} url={`/article/${slug}`} />
      <BreadcrumbSchema items={[
        { name: "Accueil", url: "/" },
        { name: "Blog", url: "/blog" },
        ...(category ? [{ name: category.name, url: `/categorie/${category.slug}` }] : []),
        { name: article.title }
      ]} />
      <ReadingProgress />
      <article className="page-enter" data-testid="article-page">
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/">Accueil</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/blog">Blog</BreadcrumbLink></BreadcrumbItem>
              {category && (<><BreadcrumbSeparator /><BreadcrumbItem><BreadcrumbLink href={`/categorie/${category.slug}`}>{category.name}</BreadcrumbLink></BreadcrumbItem></>)}
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage className="truncate max-w-[200px]">{article.title}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <header className="max-w-4xl mx-auto px-6 md:px-12 pt-8 pb-8">
          {category && <Link to={`/categorie/${category.slug}`} className="category-pill text-primary mb-4 block" data-testid="article-category">{category.name}</Link>}
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight mb-6" data-testid="article-title">{article.title}</h1>
          {article.excerpt && <div className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-3xl" dangerouslySetInnerHTML={{ __html: article.excerpt }} />}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {authors.length > 0 && authors.map((a, i) => (
              <Link key={a.slug || i} to={`/auteur/${a.slug}`} className="flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="article-author">
                <User className="h-4 w-4" /> {a.name}{a.role && <span className="text-xs opacity-70">· {a.role}</span>}
              </Link>
            ))}
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> {date}
            </div>
            {readingTime > 0 && (
              <div className="flex items-center gap-1.5" data-testid="article-reading-time">
                <Clock className="h-4 w-4" /> {readingTime} min de lecture
              </div>
            )}
            {article.views > 0 && (
              <div className="flex items-center gap-1.5" data-testid="article-views">
                <Eye className="h-4 w-4" /> {article.views} vue{article.views > 1 ? "s" : ""}
              </div>
            )}
            <div className="ml-auto">
              <ReadingToolbar article={article} onModeChange={setReadingMode} onFontSizeChange={setFontSize} onFontFamilyChange={setFontFamily} />
            </div>
          </div>
        </header>
        {article.featured_image && (
          <div className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
            <HeroImage src={article.featured_image} alt={article.title} category={category?.name} />
            {article.video_url && (
              <div className="flex justify-center mt-4">
                <a
                  href={article.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-full transition-colors"
                  data-testid="watch-here-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  Watch Here
                </a>
              </div>
            )}
          </div>
        )}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {toc.length > 0 && (
              <aside className="hidden lg:block lg:col-span-3" data-testid="toc-sidebar">
                <div className="sticky top-24">
                  <h4 className="text-xs font-mono tracking-widest uppercase text-muted-foreground mb-4">Sommaire</h4>
                  <nav className="space-y-2">
                    {toc.map((item, i) => (
                      <a key={i} href={`#${item.id}`} className={`block text-sm transition-colors hover:text-primary ${item.level === 3 ? "pl-4 text-muted-foreground" : "font-medium text-foreground"}`}>
                        {item.text}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}
            <div className={toc.length > 0 ? "lg:col-span-9" : "lg:col-span-8 lg:col-start-3"}>
              {(() => {
                const modeConfig = READING_MODES[readingMode] || READING_MODES.default;
                return (
                  <div className={`rounded-lg transition-colors duration-300 ${readingMode !== "default" ? `${modeConfig.bg} ${modeConfig.text} p-6 md:p-10 -mx-4 md:-mx-6` : ""}`} data-testid="reading-mode-container">
                    <div
                      className={`prose max-w-none ${modeConfig.prose}`}
                      style={{ fontSize: `${fontSize}%`, fontFamily: FONT_FAMILIES[fontFamily]?.value || FONT_FAMILIES.default.value }}
                      dangerouslySetInnerHTML={{ __html: processedContent }}
                      data-testid="article-content"
                    />
                  </div>
                );
              })()}
              {article.tag_details?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-12 mb-8" data-testid="article-tags">
                  {article.tag_details.map((tag) => (
                    <Link key={tag.id} to={`/recherche?q=${encodeURIComponent(tag.name)}`}>
                      <Badge variant="secondary" className="rounded-none font-mono text-xs">{tag.name}</Badge>
                    </Link>
                  ))}
                </div>
              )}
              <ShareButtons title={article.title} />
              {(article.prev_article || article.next_article) && (
                <nav className="flex items-stretch gap-4 my-12 border-y border-border py-6" data-testid="article-nav">
                  {article.prev_article ? (
                    <Link to={`/article/${article.prev_article.slug}`} className="flex-1 group flex items-center gap-3 text-left no-underline hover:text-primary transition-colors" data-testid="prev-article-link">
                      <ChevronLeft className="h-5 w-5 flex-shrink-0 text-muted-foreground group-hover:text-primary" />
                      <div>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Précédent</span>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary line-clamp-2 mt-0.5 mb-0">{article.prev_article.title}</p>
                      </div>
                    </Link>
                  ) : <div className="flex-1" />}
                  {article.next_article ? (
                    <Link to={`/article/${article.next_article.slug}`} className="flex-1 group flex items-center gap-3 text-right no-underline hover:text-primary transition-colors justify-end" data-testid="next-article-link">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Suivant</span>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary line-clamp-2 mt-0.5 mb-0">{article.next_article.title}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 flex-shrink-0 text-muted-foreground group-hover:text-primary" />
                    </Link>
                  ) : <div className="flex-1" />}
                </nav>
              )}
              <Separator className="my-12" />
              {/* Newsletter CTA */}
              <div className="bg-slate-50 border border-border rounded-lg p-8 text-center" data-testid="article-subscribe-cta">
                <h3 className="font-heading text-xl mb-2">La Toile d'OFALycée</h3>
                <p className="text-muted-foreground text-sm mb-4">Recevez nos meilleurs articles chaque mois.</p>
                <a href="https://info.ofalycee.org/subscribe" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-6 text-white text-sm" data-testid="subscribe-article-btn">S'abonner à la newsletter</Button>
                </a>
              </div>
              {article.related_articles_details?.length > 0 && (
                <section className="mt-20" data-testid="recommended-articles">
                  <h2 className="font-heading text-2xl md:text-3xl mb-3">Lectures recommandees</h2>
                  <p className="text-muted-foreground mb-8">Poursuivez votre exploration sur des sujets similaires</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                    {article.related_articles_details.map((r) => (<ArticleCard key={r.id} article={r} />))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
